import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import VistaCalcLogo from './VistaCalcLogo';
import PropertyList from './PropertyList';
import LocationManager from './LocationManager';
import { propertyAPI, PropertyInput, Property, PropertyEvent, PropertyOwnerStay } from '../services/propertyAPI';
import { Location, locationAPI } from '../services/locationAPI';
import { withLocationRates } from '../utils/locationRates';
import CashflowCharts from './charts/CashflowCharts';
import SavedAnalysesPanel from './SavedAnalysesPanel';
import ScenarioComparison from './ScenarioComparison';
import CompareConfigurations from './CompareConfigurations';
import TaxAssumptionNote from './TaxAssumptionNote';
import AssumedDefaultsNotice from './AssumedDefaultsNotice';
import EventsOwnerStaysEditor from './EventsOwnerStaysEditor';
import { parseGrowthRates, missingPricesAsOf } from '../utils/growthRates';
import { toEngineMortgage } from '../utils/mortgageVariants';

// Map the frontend event/owner-stay shape to the field names the backend engine
// reads (start/end/occ_pct/adr/prob), filtered to one property (F14, F19).
const toEngineEvents = (events: PropertyEvent[], pid?: string) =>
  events.filter((e) => e.property_id === pid).map((e) => ({
    property_id: e.property_id, start: e.start_date, end: e.end_date,
    occ_pct: e.occupancy_pct, adr: e.adr,
    prob: e.probability ?? 1,  // F19: Monte Carlo draws it
  }));
const toEngineStays = (stays: PropertyOwnerStay[], pid?: string) =>
  stays.filter((s) => s.property_id === pid).map((s) => ({
    property_id: s.property_id, start: s.start_date, end: s.end_date,
  }));

const PortfolioInvestmentCalculator: React.FC = () => {
  const navigate = useNavigate();
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingPropertyId, setEditingPropertyId] = useState<string | null>(null);
  const [analysisResults, setAnalysisResults] = useState<any>(null);
  const [lastRequest, setLastRequest] = useState<any>(null); // inputs of the last run, for saving
  const [cashflowGranularity, setCashflowGranularity] = useState<'daily' | 'monthly' | 'annual'>('annual');
  const [analysisStartDate, setAnalysisStartDate] = useState<string>('2026-01-01');
  const [analysisEndDate, setAnalysisEndDate] = useState<string>('2046-01-01');
  // F15: growth rates for the whole analysis - required, no default.
  const [euroInflationPct, setEuroInflationPct] = useState<string>('');
  const [rentGrowthPct, setRentGrowthPct] = useState<string>('');
  const [copyingProperty, setCopyingProperty] = useState<Property | null>(null);
  const [showCopyForm, setShowCopyForm] = useState(false);
  const [selectedPropertyIds, setSelectedPropertyIds] = useState<Set<string>>(new Set());
  const [locations, setLocations] = useState<Location[]>([]);
  const [debugData, setDebugData] = useState<{request: any, response: any} | null>(null);

  // Simplified form state - only essential fields
  const [formData, setFormData] = useState<PropertyInput>({
    name: '',
    address: '',
    purchase_date: '',
    rental_start_date: '',
    prices_as_of: '',
    purchase_price_eur: 0,
    purchase_costs_eur: 0,

    annual_appreciation_pct: 3.0,
    
    // Property Details
    land_percentage_pct: 20.0,
    furniture_value_eur: 15000,
    
    // Mortgage Details
    mortgage_rate_pct: 3.6,
    mortgage_term_years: 20,
    deposit_eur: 0,
    
    // Purchase Costs Breakdown
    notaire_fees_pct: 7.5,
    renovation_refurbishment_eur: 25000,
    furnishing_equipment_eur: 15000,
    legal_administrative_eur: 3000,
    property_transfer_tax_pct: 5.8,
    

    
    // Operating Costs
    management_fee_pct: 25.0,
    cleaning_per_booking_eur: 50.0,
    average_stay_length_days: 3.0,
    utilities_monthly_eur: 200.0,
    repairs_monthly_eur: 150.0,
    tourist_tax_per_guest_night_eur: 3.0,
    condo_fee_monthly_eur: 0.0,
    taxe_fonciere_annual_eur: 0.0,
    insurance_annual_eur: 0.0,
    accountant_france_annual_eur: 0.0,
    accountant_uk_annual_eur: 0.0,
    capex_provision_annual_pct: 1.5,
    
    // Location reference
    location_id: '',
    
    // Sale Details (Updated)
    sale_date: '',
    sale_price_eur: 0,
    sale_notaire_fees_pct: 0.0,
    sale_legal_fees_eur: 2000,
    sale_agency_fees_pct: 5.0,
    sale_other_fees_eur: 0,
    
    // Monte Carlo Parameters - Optional, not required for basic functionality
    occupancy_uncertainty_low_season_std_dev_pct: 8.0,
    occupancy_uncertainty_high_season_std_dev_pct: 12.0,
    occupancy_uncertainty_event_std_dev_pct: 10.0,
    occupancy_uncertainty_film_festival_std_dev_pct: 2.0,
    
    // Economic Factors
    economic_impact_std_dev_pct: 15.0,
    economic_correlation: 1.0,
    
    // Sale Price Uncertainty
    property_value_growth_std_dev_pct: 7.0,
    
    // Weather Factors
    summer_weather_std_dev_pct: 20.0,
    weather_correlation: 0.8,
    
    // Monte Carlo Settings
    mc_antithetic_variables: true,
    mc_occupancy_scenarios: 100,
    mc_scenario_jitter_std_dev_pct: 5.0,
    mc_random_seed: 12345,
    
    // Currency Risk
    eur_gbp_volatility_pct: 12.0,
    currency_correlation: 0.3,
    
    // Market Risks
    competitor_supply_increase_std_dev_pct: 20.0,
    economic_recession_impact_std_dev_pct: 30.0,
    tourism_demand_shock_std_dev_pct: 25.0,
    
    // Property Risks
    major_repair_events_annual_prob: 0.15,
    property_damage_annual_prob: 0.05,
    regulatory_changes_prob: 0.1,
    
    // Operational Risks
    management_company_failure_prob: 0.05,
    legal_issues_annual_prob: 0.08,
    bad_guest_incidents_annual_prob: 0.12,
    
    // Tax Settings
    french_tax_regime: 'Réel',
    fx_rate_income: 0.86,
    uk_income_tax_rate_pct: 40.0,
    uk_cgt_annual_exempt_gbp: 3000,
    fx_rate_purchase_uk: 0.86,
    fx_rate_sale_uk: 0.86,
    years_owned: 10,
    total_building_depreciation_claimed_eur: 0.0
  });

  // Events and owner stays
  const [events, setEvents] = useState<PropertyEvent[]>([]);
  const [ownerStays, setOwnerStays] = useState<PropertyOwnerStay[]>([]);

  useEffect(() => {
    loadProperties();
    loadLocations();
  }, []);

  const loadLocations = async () => {
    try {
      const locs = await locationAPI.getLocations();
      setLocations(locs);
    } catch (err: any) {
      console.error('Failed to load locations:', err);
    }
  };

  const loadProperties = async () => {
    try {
      setLoading(true);
      const props = await propertyAPI.getProperties();
      setProperties(props);
      // Initialize all properties as selected by default
      setSelectedPropertyIds(new Set(props.map(p => p.id!)));
      setError(null);
    } catch (err: any) {
      setError(`Failed to load properties: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof PropertyInput, value: any) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      
      // Auto-calculate rental start date when purchase date changes
      if (field === 'purchase_date' && value) {
        const purchaseDate = new Date(value);
        const rentalStartDate = new Date(purchaseDate);
        rentalStartDate.setMonth(rentalStartDate.getMonth() + 3);
        updated.rental_start_date = rentalStartDate.toISOString().split('T')[0];
      }
      
      return updated;
    });
  };

  const handleAddProperty = async () => {
    try {
      setLoading(true);
      const newProperty = await propertyAPI.createProperty(formData);
      
      // Note: Events and owner stays functionality will be added in future updates
      // For now, properties are created with basic information only

      setProperties(prev => [newProperty, ...prev]);
      // Add the new property to the selected set
      setSelectedPropertyIds(prev => new Set([...prev, newProperty.id!]));
      setFormData({
        name: '', address: '', purchase_date: '', rental_start_date: '', prices_as_of: '', purchase_price_eur: 0,
        purchase_costs_eur: 0,
        annual_appreciation_pct: 3.0,
        land_percentage_pct: 20.0, furniture_value_eur: 15000, mortgage_rate_pct: 3.6,
        mortgage_term_years: 20, deposit_eur: 0, notaire_fees_pct: 7.5,
        renovation_refurbishment_eur: 25000, furnishing_equipment_eur: 15000,
        legal_administrative_eur: 3000, property_transfer_tax_pct: 5.8,
        sale_notaire_fees_pct: 0.0,
        sale_legal_fees_eur: 2000, sale_agency_fees_pct: 5.0, sale_other_fees_eur: 0, management_fee_pct: 25.0,
        cleaning_per_booking_eur: 50.0, average_stay_length_days: 3.0, utilities_monthly_eur: 200.0,
        repairs_monthly_eur: 150.0, tourist_tax_per_guest_night_eur: 3.0,
        condo_fee_monthly_eur: 0.0, taxe_fonciere_annual_eur: 0.0,
        insurance_annual_eur: 0.0, accountant_france_annual_eur: 0.0,
        accountant_uk_annual_eur: 0.0, location_id: '',
        occupancy_uncertainty_low_season_std_dev_pct: 8.0, occupancy_uncertainty_high_season_std_dev_pct: 12.0,
        occupancy_uncertainty_event_std_dev_pct: 10.0, occupancy_uncertainty_film_festival_std_dev_pct: 2.0,
        economic_impact_std_dev_pct: 15.0, economic_correlation: 1.0,
        property_value_growth_std_dev_pct: 7.0, summer_weather_std_dev_pct: 20.0, weather_correlation: 0.8,
        mc_antithetic_variables: true, mc_occupancy_scenarios: 100, mc_scenario_jitter_std_dev_pct: 5.0,
        mc_random_seed: 12345, eur_gbp_volatility_pct: 12.0, currency_correlation: 0.3,
        competitor_supply_increase_std_dev_pct: 20.0, economic_recession_impact_std_dev_pct: 30.0,
        tourism_demand_shock_std_dev_pct: 25.0, major_repair_events_annual_prob: 0.15,
        property_damage_annual_prob: 0.05, regulatory_changes_prob: 0.1, management_company_failure_prob: 0.05,
        legal_issues_annual_prob: 0.08, bad_guest_incidents_annual_prob: 0.12, french_tax_regime: 'Réel',
        fx_rate_income: 0.86, fx_rate_purchase_uk: 0.86, fx_rate_sale_uk: 0.86, years_owned: 10, uk_income_tax_rate_pct: 40.0, uk_cgt_annual_exempt_gbp: 3000,
        total_building_depreciation_claimed_eur: 0.0
      });
      setEvents([]);
      setOwnerStays([]);
      setShowAddForm(false);
      setShowCopyForm(false);
      setCopyingProperty(null);
      setError(null);
    } catch (err: any) {
      setError(`Failed to add property: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProperty = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this property?')) return;

    try {
      await propertyAPI.deleteProperty(id);
      setProperties(prev => prev.filter(p => p.id !== id));
      // Remove the deleted property from the selected set
      setSelectedPropertyIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
      setError(null);
    } catch (err: any) {
      setError(`Failed to delete property: ${err.message}`);
    }
  };

  const handleEditProperty = (property: Property) => {
    setEditingPropertyId(property.id!);
    
    // Debug: Log what fields the property actually has
    console.log('Property being edited:', {
      id: property.id,
      name: property.name,
      sale_fields: {
        sale_notaire_fees_pct: property.sale_notaire_fees_pct,
        sale_legal_fees_eur: property.sale_legal_fees_eur,
        sale_agency_fees_pct: property.sale_agency_fees_pct,
        sale_other_fees_eur: property.sale_other_fees_eur,
        // Check for old fields
        sale_agent_commission_pct: (property as any).sale_agent_commission_pct,
      }
    });
    
    // Ensure the property data has the correct sale field structure
    const normalizedProperty = {
      ...property,
      // Map old field names to new ones if they exist
      sale_legal_fees_eur: property.sale_legal_fees_eur ?? 2000,
      sale_agency_fees_pct: property.sale_agency_fees_pct ?? 5.0,
      sale_other_fees_eur: property.sale_other_fees_eur ?? 0,
      // Handle potential old field names from database
      ...((property as any).sale_agent_commission_pct && { 
        sale_agency_fees_pct: (property as any).sale_agent_commission_pct 
      }),
    };
    
    console.log('Normalized property data:', normalizedProperty);
    setFormData(normalizedProperty);
  };

  const handleUpdateProperty = async (id: string, data: Partial<PropertyInput>) => {
    try {
      setLoading(true);
      

      
      const updatedProperty = await propertyAPI.updateProperty(id, data);
      if (updatedProperty) {
        setProperties(prev => prev.map(p => p.id === id ? updatedProperty : p));
        setEditingPropertyId(null);
        resetFormData();
        setError(null);
      }
    } catch (err: any) {
      setError(`Failed to update property: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const resetFormData = () => {
    setFormData({
      name: '', address: '', purchase_date: '', rental_start_date: '', prices_as_of: '', purchase_price_eur: 0,
      purchase_costs_eur: 0,
      annual_appreciation_pct: 3.0,
      land_percentage_pct: 20.0, furniture_value_eur: 15000, mortgage_rate_pct: 3.6,
      mortgage_term_years: 20, deposit_eur: 0, notaire_fees_pct: 7.5,
      renovation_refurbishment_eur: 25000, furnishing_equipment_eur: 15000,
      legal_administrative_eur: 3000, property_transfer_tax_pct: 5.8,
      sale_notaire_fees_pct: 0.0,
      sale_legal_fees_eur: 2000, sale_agency_fees_pct: 5.0, sale_other_fees_eur: 0, management_fee_pct: 25.0,
      cleaning_per_booking_eur: 50.0, average_stay_length_days: 3.0, utilities_monthly_eur: 200.0,
      repairs_monthly_eur: 150.0, tourist_tax_per_guest_night_eur: 3.0,
      condo_fee_monthly_eur: 0.0, taxe_fonciere_annual_eur: 0.0,
      insurance_annual_eur: 0.0, accountant_france_annual_eur: 0.0,
              accountant_uk_annual_eur: 0.0, location_id: '',
      occupancy_uncertainty_low_season_std_dev_pct: 8.0, occupancy_uncertainty_high_season_std_dev_pct: 12.0,
      occupancy_uncertainty_event_std_dev_pct: 10.0, occupancy_uncertainty_film_festival_std_dev_pct: 2.0,
      economic_impact_std_dev_pct: 15.0, economic_correlation: 1.0,
      property_value_growth_std_dev_pct: 7.0, summer_weather_std_dev_pct: 20.0, weather_correlation: 0.8,
      mc_antithetic_variables: true, mc_occupancy_scenarios: 100, mc_scenario_jitter_std_dev_pct: 5.0,
      mc_random_seed: 12345, eur_gbp_volatility_pct: 12.0, currency_correlation: 0.3,
      competitor_supply_increase_std_dev_pct: 20.0, economic_recession_impact_std_dev_pct: 30.0,
      tourism_demand_shock_std_dev_pct: 25.0, major_repair_events_annual_prob: 0.15,
      property_damage_annual_prob: 0.05, regulatory_changes_prob: 0.1, management_company_failure_prob: 0.05,
      legal_issues_annual_prob: 0.08, bad_guest_incidents_annual_prob: 0.12, french_tax_regime: 'Réel',
      fx_rate_income: 0.86, fx_rate_purchase_uk: 0.86, fx_rate_sale_uk: 0.86, years_owned: 10, uk_income_tax_rate_pct: 40.0, uk_cgt_annual_exempt_gbp: 3000,
      total_building_depreciation_claimed_eur: 0.0
    });
    setEvents([]);
    setOwnerStays([]);
  };

  const handleCancelEdit = () => {
    setEditingPropertyId(null);
    resetFormData();
    setError(null);
  };

  const handlePropertySelectionChange = (propertyId: string, isSelected: boolean) => {
    setSelectedPropertyIds(prev => {
      const newSet = new Set(prev);
      if (isSelected) {
        newSet.add(propertyId);
      } else {
        newSet.delete(propertyId);
      }
      return newSet;
    });
  };

  const handleSelectAllProperties = () => {
    setSelectedPropertyIds(new Set(properties.map(p => p.id!)));
  };

  const handleDeselectAllProperties = () => {
    setSelectedPropertyIds(new Set());
  };

  const handleCopyProperty = (property: Property) => {
    setCopyingProperty(property);
    setFormData({
      ...property,
      name: `${property.name} (Copy)`
    });
    setShowCopyForm(true);
    setShowAddForm(false);
  };



  const handleAnalyzePortfolio = async () => {
    if (properties.length === 0) {
      setError('No properties to analyze');
      return;
    }

    // Filter properties based on selection
    const selectedProperties = properties.filter(p => selectedPropertyIds.has(p.id!));
    
    if (selectedProperties.length === 0) {
      setError('Please select at least one property to analyze');
      return;
    }

    const growth = parseGrowthRates({ euroInflationPct, rentGrowthPct });
    if (!growth.ok) {
      setError(growth.error);
      return;
    }
    const undated = missingPricesAsOf(selectedProperties);
    if (undated.length > 0) {
      setError(`Set "Prices As Of" (the date its rents and costs were entered) on: ${undated.join(', ')}`);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      console.log('Starting portfolio analysis...');
      console.log('Selected properties:', selectedProperties);
      console.log('Analysis params:', { start_date: analysisStartDate, end_date: analysisEndDate, granularity: cashflowGranularity });
      
      // Send comprehensive data to Railway backend for analysis
      const requestBody = {
        properties: selectedProperties.map(p => withLocationRates(toEngineMortgage({
          ...p,
          events: toEngineEvents(events, p.id),
          owner_stays: toEngineStays(ownerStays, p.id)
        }), locations)),
        start_date: analysisStartDate,
        end_date: analysisEndDate,
        granularity: cashflowGranularity,
        ...growth.rates,
        analysis_date: new Date().toISOString()
      };
      setLastRequest(requestBody);

      console.log('Request body:', requestBody);
      
      const apiBase = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
      const response = await fetch(`${apiBase}/api/v1/portfolio/analyze`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      console.log('Response status:', response.status);
      console.log('Response ok:', response.ok);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Response error:', errorText);
        throw new Error(`Analysis failed: ${response.status} - ${errorText}`);
      }

      const results = await response.json();
      console.log('Analysis results:', results);
      console.log('First month data structure:', results.monthly_cashflow_breakdown?.[0]);
      console.log('Available fields in first month:', results.monthly_cashflow_breakdown?.[0] ? Object.keys(results.monthly_cashflow_breakdown[0]) : 'No data');
      
      // Store debug data for export
      setDebugData({
        request: requestBody,
        response: results
      });
      
             // Transform backend data structure to match frontend expectations
       const transformedResults = {
         ...results,
         cashflow_breakdown: results.monthly_cashflow_breakdown?.map((month: any, index: number) => {
           // Calculate cumulative cashflow
           let cumulative = 0;
           if (index === 0) {
             cumulative = month.net_cashflow;
           } else {
             // Add current month's net cashflow to previous cumulative
             const previousCumulative = results.monthly_cashflow_breakdown
               .slice(0, index)
               .reduce((sum: number, prevMonth: any) => sum + prevMonth.net_cashflow, 0);
             cumulative = previousCumulative + month.net_cashflow;
           }
           
                     return {
            date: month.month,
            event_days_occupied: month.event_days_occupied || 0,
            event_days_unoccupied: month.event_days_unoccupied || 0,
            non_event_days_occupied: month.non_event_days_occupied || 0,
            non_event_days_unoccupied: month.non_event_days_unoccupied || 0,
            owner_days: month.owner_days || 0,
            revenue: month.total_income,
            average_daily_rate: month.average_daily_rate || 0,
                            occupancy_rate: month.occupancy_rate_pct || 0,
            total_nights: (() => {
              console.log('Month total_nights value:', month.total_nights);
              console.log('Month total_nights type:', typeof month.total_nights);
              return month.total_nights || 0;
            })(),
            management_fees: month.management_fees || 0,
            utilities: month.utilities || 0,
            repairs: month.repairs || 0,
            cleaning: month.cleaning || 0,
            tourist_tax: month.tourist_tax || 0,
            condo_fees: month.condo_fees || 0,
            taxe_fonciere: month.taxe_fonciere || 0,
            insurance: month.insurance || 0,
            accountant_france: month.accountant_france || 0,
            accountant_uk: month.accountant_uk || 0,
            mortgage_interest: month.mortgage_interest || 0,
            mortgage_principal: month.mortgage_principal || 0,
            capex_provision: month.capex_provision || 0,
            deposit_outflow: month.deposit_outflow || 0,
                                notaire_fees: month.notaire_fees || 0,
                    legal_costs: month.legal_costs || 0,
                    transfer_tax: month.transfer_tax || 0,
            renovation_costs: month.renovation_costs || 0,
            furnishing_costs: month.furnishing_costs || 0,
            sale_proceeds: month.sale_proceeds || 0,
            sale_price: month.sale_price || 0,
            sale_costs: month.sale_costs || 0,
            mortgage_redemption: month.mortgage_redemption || 0,
            unpaid_tax_settled_eur: month.unpaid_tax_settled_eur || 0,
            earnings_before_tax: month.earnings_before_tax || 0,
            french_income_tax: month.french_income_tax || 0,
            french_social_levy: month.french_social_levy || 0,
            uk_income_tax_eur: month.uk_income_tax_eur || 0,
            french_cgt_surtax: month.french_cgt_surtax || 0,
            total_expenses: month.total_expenses,
            net_cashflow: month.net_cashflow,
            cumulative_cashflow: cumulative,
            cumulative_after_tax: month.cumulative_after_tax || 0
          };
         }) || [],
        total_revenue: results.monthly_cashflow_breakdown?.reduce((sum: number, month: any) => sum + month.total_income, 0) || 0,
        total_expenses: results.monthly_cashflow_breakdown?.reduce((sum: number, month: any) => sum + month.total_expenses, 0) || 0,
        total_net_cashflow: results.monthly_cashflow_breakdown?.reduce((sum: number, month: any) => sum + month.net_cashflow, 0) || 0,
        average_monthly_cashflow: results.total_monthly_cashflow || 0
      };
      
      setAnalysisResults(transformedResults);
      setError(null);
      
          } catch (err: any) {
        console.error('Analysis error:', err);
        setError(`Failed to analyze portfolio: ${err.message}`);
        
        // Create a fallback result for testing/development
        const fallbackResults = {
          total_investment: properties.reduce((sum, p) => sum + p.purchase_price_eur, 0),
          total_projected_value: properties.reduce((sum, p) => sum + p.purchase_price_eur * (1 + (p.annual_appreciation_pct || 0) / 100), 0),
          total_return: 0,
          annualized_return_pct: 0,
          total_revenue: 0,
          total_expenses: 0,
          total_net_cashflow: 0,
          average_monthly_cashflow: 0,
          cashflow_breakdown: [],
          message: "Backend analysis failed - showing fallback data"
        };
        
        setAnalysisResults(fallbackResults);
      } finally {
        setLoading(false);
      }
    };

  // Helper function to format currency values with proper negative styling (no decimals)
  const formatCurrency = (value: number, showSign: boolean = false) => {
    const absValue = Math.abs(value);
    const formatted = Math.round(absValue).toLocaleString();
    const isNegative = value < 0;
    
    if (showSign) {
      return `${isNegative ? '-' : '+'}€${formatted}`;
    }
    
    return `€${formatted}`;
  };

  // Export functions
  const exportToCSV = () => {
    if (!analysisResults?.cashflow_breakdown) return;
    
    const headers = [
      'Period', 'Event Days Occupied', 'Event Days Unoccupied', 'Non Event Days Occupied', 
      'Non Event Days Unoccupied', 'Owner Days', 'ADR', 'Occupancy %', 'Nights', 'Revenue',
      'Management Fees', 'Utilities', 'Repairs', 'Cleaning', 'Tourist Tax', 'Condo Fees',
      'Taxe Fonciere', 'Insurance', 'Accountant France', 'Accountant UK', 'Mortgage Interest',
      'Mortgage Principal', 'Capex Provision', 'Deposit Outflow', 'Purchase Costs',
      'Renovation Costs', 'Furnishing Costs', 'Sale Proceeds', 'Sale Price', 'Sale Costs',
      'Mortgage Redemption', 'Earnings Before Tax', 'French Income Tax', 'French Social Levy', 'UK Income Tax', 'Unpaid Tax Settled At End',
      'Total Expenses', 'Net Cashflow', 'Cumulative Cashflow (pre-tax)', 'Cumulative After-Tax'
    ];
    
    const csvContent = [
      headers.join(','),
      ...analysisResults.cashflow_breakdown.map((period: any) => [
        period.date || period.month || period.year,
        period.event_days_occupied || 0,
        period.event_days_unoccupied || 0,
        period.non_event_days_occupied || 0,
        period.non_event_days_unoccupied || 0,
        period.owner_days || 0,
        period.average_daily_rate || 0,
                        period.occupancy_rate || 0,
        period.total_nights || 0,
        period.revenue || 0,
        period.management_fees || 0,
        period.utilities || 0,
        period.repairs || 0,
        period.cleaning || 0,
        period.tourist_tax || 0,
        period.condo_fees || 0,
        period.taxe_fonciere || 0,
        period.insurance || 0,
        period.accountant_france || 0,
        period.accountant_uk || 0,
        period.mortgage_interest || 0,
        period.mortgage_principal || 0,
        period.capex_provision || 0,
        period.deposit_outflow || 0,
        period.purchase_costs || 0,
        period.renovation_costs || 0,
        period.furnishing_costs || 0,
        period.sale_proceeds || 0,
        period.sale_price || 0,
        period.sale_costs || 0,
        period.mortgage_redemption || 0,
        period.earnings_before_tax || 0,
        period.french_income_tax || 0,
        period.french_social_levy || 0,
        period.uk_income_tax_eur || 0,
        period.unpaid_tax_settled_eur || 0,
        period.total_expenses || 0,
        period.net_cashflow || 0,
        period.cumulative_cashflow || 0,
        period.cumulative_after_tax || 0
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `vistacalc_cashflow_${cashflowGranularity}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  

  const exportToPDF = () => {
    // For now, we'll use a simple approach to capture the page content
    // In a production environment, you'd want to use a proper PDF library like jsPDF or html2pdf
    const element = document.getElementById('portfolio-calculator');
    if (!element) return;
    
    // Create a new window with the content
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>VistaCalc - Portfolio Investment Calculator Report</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .header { text-align: center; margin-bottom: 30px; }
            .logo { font-size: 24px; font-weight: bold; color: #0f766e; }
            .summary { margin-bottom: 20px; }
            .summary-grid { display: grid; grid-template-columns: repeat(5, 1fr); gap: 20px; margin-bottom: 20px; }
            .summary-item { text-align: center; padding: 10px; background: #f3f4f6; border-radius: 8px; }
            .summary-value { font-size: 18px; font-weight: bold; color: #0f766e; }
            .summary-label { font-size: 12px; color: #6b7280; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 20px; font-size: 10px; }
            th, td { border: 1px solid #d1d5db; padding: 4px; text-align: right; }
            th { background: #0f766e; color: white; font-weight: bold; }
            .negative { color: #dc2626; }
            .positive { color: #059669; }
            .charts { margin-top: 20px; }
            .chart { margin-bottom: 20px; }
            .chart-title { font-weight: bold; margin-bottom: 10px; }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="logo">VistaCalc by Brancind</div>
            <h1>Portfolio Investment Calculator Report</h1>
            <p>Generated on ${new Date().toLocaleDateString()}</p>
          </div>
          
          <div class="summary">
            <h2>Portfolio Summary</h2>
            <div class="summary-grid">
              <div class="summary-item">
                <div class="summary-value">€${analysisResults?.total_investment?.toLocaleString() || 0}</div>
                <div class="summary-label">Total Investment</div>
              </div>
              <div class="summary-item">
                <div class="summary-value">€${analysisResults?.total_projected_value?.toLocaleString() || 0}</div>
                <div class="summary-label">Projected Value</div>
              </div>
              <div class="summary-item">
                <div class="summary-value">€${analysisResults?.total_return?.toLocaleString() || 0}</div>
                <div class="summary-label">Total Return (gross)</div>
              </div>
              <div class="summary-item">
                <div class="summary-value">${analysisResults?.annualized_return_pct?.toFixed(2) || 0}%</div>
                <div class="summary-label">Annualized Return (gross)</div>
              </div>
              <div class="summary-item">
                <div class="summary-value">€${Math.round(analysisResults?.final_after_tax_total || 0).toLocaleString()}</div>
                <div class="summary-label">After-Tax Total</div>
              </div>
              <div class="summary-item">
                <div class="summary-value">${analysisResults?.irr_after_tax_pct != null ? analysisResults.irr_after_tax_pct.toFixed(2) + '%' : 'n/a'}</div>
                <div class="summary-label">IRR after tax (a year)</div>
              </div>
            </div>
          </div>
          
          <h2>Cashflow Analysis - ${cashflowGranularity.charAt(0).toUpperCase() + cashflowGranularity.slice(1)} Breakdown</h2>
          ${element.outerHTML}
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.print();
  };

  const exportForDebugging = () => {
    if (!debugData) {
      alert('No debug data available. Please run an analysis first.');
      return;
    }

    const debugInfo = {
      timestamp: new Date().toISOString(),
      request: debugData.request,
      response: debugData.response,
      analysisResults: analysisResults
    };

    const blob = new Blob([JSON.stringify(debugInfo, null, 2)], { type: 'application/json' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
              link.setAttribute('download', `vistacalc_debug_${new Date().toISOString().replace(/[:.]/g, '-')}.json`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const renderInputField = (field: keyof PropertyInput, label: string, type: string = 'text', placeholder?: string) => (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label}
      </label>
      <input
        type={type}
        value={String(formData[field] || '')}
        onChange={(e) => handleInputChange(field, type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value)}
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-vista-teal-500"
        placeholder={placeholder}
      />
    </div>
  );

  const renderLocationSelector = () => (
    <div className="mb-6">
      <h4 className="text-lg font-semibold text-gray-800 mb-3">Location</h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Location Name
          </label>
          <select
            value={formData.location_id || ''}
            onChange={(e) => handleInputChange('location_id', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-vista-teal-500"
          >
            <option value="">Select a location...</option>
            {locations.map((location) => (
              <option key={location.id} value={location.id}>
                {location.name}
              </option>
            ))}
          </select>
          <p className="text-sm text-gray-500 mt-1">
            Select a location to inherit monthly ADR and occupancy data
          </p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Or enter custom location name
          </label>
          <input
            type="text"
            value={formData.location_id || ''}
            onChange={(e) => handleInputChange('location_id', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-vista-teal-500"
            placeholder="e.g., Cannes City Center"
          />
        </div>
      </div>
    </div>
  );

  return (
    <div id="portfolio-calculator" className="min-h-screen bg-vista-silver-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-vista-silver-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/')}
                className="text-vista-silver-600 hover:text-vista-teal-500 transition-colors"
              >
                ← Back to Home
              </button>
              <VistaCalcLogo size="sm" />
            </div>
            <h1 className="text-xl font-bold text-vista-silver-900">Portfolio Investment Calculator</h1>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}

      {/* Analysis Controls */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-2xl font-semibold text-gray-800 mb-4">Portfolio Analysis Controls</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
            <input
              type="date"
              value={analysisStartDate}
              onChange={(e) => setAnalysisStartDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-vista-teal-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
            <input
              type="date"
              value={analysisEndDate}
              onChange={(e) => setAnalysisEndDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-vista-teal-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2" title="Annual growth of running costs from each property's Prices As Of date">Euro Inflation (%/yr)</label>
            <input
              type="number"
              step="0.1"
              placeholder="required, e.g. 2"
              value={euroInflationPct}
              onChange={(e) => setEuroInflationPct(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-vista-teal-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2" title="Annual growth of nightly and event rates from each property's Prices As Of date">Rent Growth (%/yr)</label>
            <input
              type="number"
              step="0.1"
              placeholder="required, e.g. 2"
              value={rentGrowthPct}
              onChange={(e) => setRentGrowthPct(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-vista-teal-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Cashflow Granularity</label>
            <select
              value={cashflowGranularity}
              onChange={(e) => setCashflowGranularity(e.target.value as 'daily' | 'monthly' | 'annual')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-vista-teal-500"
            >
              <option value="daily">Daily</option>
              <option value="monthly">Monthly</option>
              <option value="annual">Annual</option>
            </select>
          </div>
          <div className="flex items-end gap-2">
            <button
              onClick={handleAnalyzePortfolio}
              disabled={selectedPropertyIds.size === 0 || loading}
              className="flex-1 bg-vista-teal-600 text-white px-6 py-2 rounded-md hover:bg-vista-teal-700 transition-colors disabled:opacity-50"
            >
              {loading ? 'Analyzing...' : 'Analyze Portfolio'}
            </button>
            <button
              onClick={() => exportForDebugging()}
              className="bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700 transition-colors"
              title="Export debug data (request/response from backend)"
            >
              Debug
            </button>
          </div>
        </div>
      </div>

      {/* Events & owner stays editor (F14): user-defined premium dates / blocked weeks */}
      <EventsOwnerStaysEditor
        properties={properties.filter((p) => selectedPropertyIds.has(p.id!))}
        startDate={analysisStartDate}
        endDate={analysisEndDate}
        events={events}
        onEventsChange={setEvents}
        ownerStays={ownerStays}
        onOwnerStaysChange={setOwnerStays}
      />

      {/* Saved analyses: save the current run, or load/delete a past one */}
      <SavedAnalysesPanel
        currentResult={analysisResults}
        saveMeta={{ granularity: cashflowGranularity, start_date: analysisStartDate, end_date: analysisEndDate, request: lastRequest }}
        onLoad={(result) => setAnalysisResults(result)}
      />

      {/* Scenario comparison: what-if assumptions vs base */}
      <ScenarioComparison
        properties={properties
          .filter((p) => selectedPropertyIds.has(p.id!))
          .map((p) => withLocationRates(toEngineMortgage({
            ...p,
            events: toEngineEvents(events, p.id),
            owner_stays: toEngineStays(ownerStays, p.id),
          }), locations))}
        startDate={analysisStartDate}
        endDate={analysisEndDate}
        granularity={cashflowGranularity}
        growthRates={{ euroInflationPct, rentGrowthPct }}
      />

      {/* F18: compare 2-4 selected properties as named configurations */}
      <CompareConfigurations
        properties={properties
          .filter((p) => selectedPropertyIds.has(p.id!))
          .map((p) => withLocationRates(toEngineMortgage({
            ...p,
            events: toEngineEvents(events, p.id),
            owner_stays: toEngineStays(ownerStays, p.id),
          }), locations))}
        startDate={analysisStartDate}
        endDate={analysisEndDate}
        granularity={cashflowGranularity}
        growthRates={{ euroInflationPct, rentGrowthPct }}
      />

      {/* Portfolio Summary */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-semibold text-gray-800">Portfolio Summary</h2>
          <a 
            href="/monte-carlo" 
            className="bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700 transition-colors text-sm"
          >
            Advanced Monte Carlo Analysis
          </a>
        </div>
                 <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
           <div className="text-center">
             <div className="text-3xl font-bold text-vista-teal-600">{selectedPropertyIds.size}</div>
             <div className="text-sm text-gray-600">Selected Properties</div>
           </div>
           <div className="text-center">
             <div className="text-3xl font-bold text-vista-teal-600">
               €{properties.filter(p => selectedPropertyIds.has(p.id!)).reduce((sum, p) => sum + p.purchase_price_eur, 0).toLocaleString()}
             </div>
             <div className="text-sm text-gray-600">Selected Investment</div>
           </div>
           <div className="text-center">
             <div className="text-3xl font-bold text-vista-teal-600">
               {selectedPropertyIds.size > 0 ? (properties.filter(p => selectedPropertyIds.has(p.id!)).reduce((sum, p) => sum + (p.annual_appreciation_pct || 0), 0) / selectedPropertyIds.size).toFixed(1) : 0}%
             </div>
             <div className="text-sm text-gray-600">Avg Appreciation</div>
           </div>
         </div>
        
        <div className="mt-6">
          <button
            onClick={() => setShowAddForm(true)}
            className="bg-vista-teal-600 text-white px-6 py-2 rounded-md hover:bg-vista-teal-700 transition-colors"
          >
            Add Property
          </button>
        </div>
      </div>

      {/* Add/Edit Property Form */}
      {showAddForm && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-2xl font-semibold text-gray-800 mb-6">
            Add New Property
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Basic Property Info */}
            <div>
              <h3 className="text-lg font-semibold text-gray-700 mb-4">Basic Information</h3>
              {renderInputField('name', 'Property Name')}
              {renderInputField('address', 'Address')}
              {renderInputField('purchase_date', 'Purchase Date', 'date')}
              {renderInputField('rental_start_date', 'Rental Start Date', 'date')}
              {renderInputField('prices_as_of', 'Prices As Of (date rents & costs were entered)', 'date')}
              {renderInputField('purchase_price_eur', 'Purchase Price (€)', 'number')}
              {renderInputField('annual_appreciation_pct', 'Property Growth (%/yr, nominal - price to purchase and sale)', 'number')}
            </div>

            {/* Purchase Costs */}
            <div>
              <h3 className="text-lg font-semibold text-gray-700 mb-4">Purchase Costs</h3>
              {renderInputField('notaire_fees_pct', 'Notaire Fees (%)', 'number')}
              {renderInputField('renovation_refurbishment_eur', 'Renovation & Refurbishment (€)', 'number')}
              {renderInputField('furnishing_equipment_eur', 'Furnishing & Equipment (€)', 'number')}
              {renderInputField('legal_administrative_eur', 'Legal & Administrative (€)', 'number')}
              {renderInputField('property_transfer_tax_pct', 'Property Transfer Tax (%)', 'number')}
            </div>

            {/* Sale Details */}
            <div>
              <h3 className="text-lg font-semibold text-gray-700 mb-4">Sale Details</h3>
              {renderInputField('sale_date', 'Sale Date', 'date')}
              {renderInputField('sale_price_eur', 'Sale Price (€)', 'number')}

              {renderInputField('sale_notaire_fees_pct', 'Sale Notaire Fees (%)', 'number')}
              {renderInputField('sale_legal_fees_eur', 'Sale Legal Fees (€)', 'number')}
              {renderInputField('sale_agency_fees_pct', 'Sale Agency Fees (%)', 'number')}
              {renderInputField('sale_other_fees_eur', 'Other Sale Fees (€)', 'number')}
            </div>

            {/* Mortgage Details */}
            <div>
              <h3 className="text-lg font-semibold text-gray-700 mb-4">Mortgage Details</h3>
              <p className="text-sm text-gray-600 mb-4">
                Mortgage payments are calculated monthly and applied on the 1st of each month.
              </p>
              {renderInputField('mortgage_rate_pct', 'Mortgage Rate (%)', 'number')}
              {renderInputField('mortgage_term_years', 'Mortgage Term (Years)', 'number')}
              {renderInputField('mortgage_interest_only_years', 'Interest-Only Years (0 = repayment from the start)', 'number')}
              {renderInputField('mortgage_step_after_years', 'Rate Step After (Years, 0 = none)', 'number')}
              {renderInputField('mortgage_step_rate_pct', 'Rate After the Step (%)', 'number')}
              {renderInputField('mortgage_arrangement_fee_eur', 'Arrangement Fee (€)', 'number')}
              <div className="mb-4 flex items-center gap-2">
                <input
                  type="checkbox"
                  id="mortgage_fee_added_to_loan"
                  checked={Boolean(Number(formData.mortgage_fee_added_to_loan))}
                  onChange={(e) => handleInputChange('mortgage_fee_added_to_loan', e.target.checked ? 1 : 0)}
                />
                <label htmlFor="mortgage_fee_added_to_loan" className="text-sm text-gray-700">Fee added to the loan (otherwise paid in cash at purchase)</label>
              </div>
              {renderInputField('deposit_eur', 'Deposit (€)', 'number')}
            </div>

            {/* Operating Costs */}
            <div>
              <h3 className="text-lg font-semibold text-gray-700 mb-4">Operating Costs</h3>
              <p className="text-sm text-gray-600 mb-4">
                Monthly costs are applied on the 1st of each month. Annual costs are split into 12 equal monthly payments on the 1st of each month.
              </p>
              {renderInputField('management_fee_pct', 'Management Fee (%)', 'number')}
              {renderInputField('cleaning_per_booking_eur', 'Cleaning per Booking (€)', 'number')}
              {renderInputField('average_stay_length_days', 'Average Stay Length (days)', 'number')}
              {renderInputField('utilities_monthly_eur', 'Utilities Monthly (€) - Applied 1st of month', 'number')}
              {renderInputField('repairs_monthly_eur', 'Repairs Monthly (€) - Applied 1st of month', 'number')}
              {renderInputField('tourist_tax_per_guest_night_eur', 'Tourist Tax per Guest-Night (€)', 'number')}
              {renderInputField('condo_fee_monthly_eur', 'Condo Fees Monthly (€) - Applied 1st of month', 'number')}
              {renderInputField('taxe_fonciere_annual_eur', 'Taxe Foncière Annual (€) - Split into 12 monthly payments', 'number')}
              {renderInputField('insurance_annual_eur', 'Insurance Annual (€) - Split into 12 monthly payments', 'number')}
              {renderInputField('accountant_france_annual_eur', 'French Accountant Annual (€) - Split into 12 monthly payments', 'number')}
              {renderInputField('accountant_uk_annual_eur', 'UK Accountant Annual (€) - Split into 12 monthly payments', 'number')}
              {renderInputField('capex_provision_annual_pct', 'Capex Provision Annual (%) - Split into 12 monthly payments', 'number')}
            </div>
          </div>

          {/* Location Selection */}
          {renderLocationSelector()}

          {/* Advanced Settings */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-700 mb-4">Monte Carlo Parameters</h3>
              {renderInputField('occupancy_uncertainty_low_season_std_dev_pct', 'Low Season Std Dev (%)', 'number')}
              {renderInputField('occupancy_uncertainty_high_season_std_dev_pct', 'High Season Std Dev (%)', 'number')}
              {renderInputField('economic_impact_std_dev_pct', 'Economic Impact Std Dev (%)', 'number')}
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-700 mb-4">Risk Factors</h3>
              {renderInputField('major_repair_events_annual_prob', 'Major Repair Events (Annual Prob)', 'number')}
              {renderInputField('property_damage_annual_prob', 'Property Damage (Annual Prob)', 'number')}
              {renderInputField('management_company_failure_prob', 'Management Company Failure (Prob)', 'number')}
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-700 mb-4">Tax Settings</h3>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  French Tax Regime
                </label>
                                 <select
                   value={formData.french_tax_regime || 'Réel'}
                   onChange={(e) => handleInputChange('french_tax_regime', e.target.value)}
                   className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-vista-teal-500"
                 >
                   <option value="Réel">Réel</option>
                   <option value="Micro-BIC 50%">Micro-BIC 50%</option>
                   <option value="Micro-BIC 71% (classé)">Micro-BIC 71% (classé)</option>
                   <option value="Micro-BIC 30%">Micro-BIC 30%</option>
                   <option value="Réel + URSSAF">Réel + URSSAF</option>
                 </select>
              </div>
              {renderInputField('uk_income_tax_rate_pct', 'UK Income Tax Rate (%)', 'number')}
              {renderInputField('uk_cgt_annual_exempt_gbp', 'UK CGT Annual Exempt (£)', 'number')}
              {renderInputField('fx_rate_income', 'FX Rate Income (€/£)', 'number')}
              {renderInputField('years_owned', 'Years Owned', 'number')}
              {renderInputField('land_percentage_pct', 'Land Allocation % (excluded from building depreciation)', 'number')}
              {renderInputField('furniture_value_eur', 'Furniture Value for Depreciation (€)', 'number')}
              {renderInputField('purchase_agency_fees_eur', 'Purchase Agency Fees (€, added to CGT base)', 'number')}
              {renderInputField('capitalised_works_eur', 'Capitalised Works (€, reduce French capital gain)', 'number')}
            </div>
          </div>

          <div className="flex gap-3 mt-6">
            <button
              onClick={handleAddProperty}
              disabled={loading}
              className="bg-vista-teal-600 text-white px-6 py-2 rounded-md hover:bg-vista-teal-700 transition-colors"
            >
              {loading ? 'Creating...' : 'Add Property'}
            </button>
            <button
              onClick={() => {
                setShowAddForm(false);
                resetFormData();
              }}
              className="bg-gray-500 text-white px-6 py-2 rounded-md hover:bg-gray-600 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Location Management */}
      <LocationManager onLocationsChange={loadLocations} />

      {/* Properties List */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-semibold text-gray-800">Properties</h2>
          <div className="flex gap-2">
            <button
              onClick={handleSelectAllProperties}
              className="bg-vista-teal-600 text-white px-3 py-1 rounded text-sm hover:bg-vista-teal-700 transition-colors"
            >
              Select All
            </button>
            <button
              onClick={handleDeselectAllProperties}
              className="bg-gray-600 text-white px-3 py-1 rounded text-sm hover:bg-gray-700 transition-colors"
            >
              Deselect All
            </button>
          </div>
        </div>
        <PropertyList
          properties={properties}
          loading={loading}
          selectedPropertyIds={selectedPropertyIds}
          onPropertySelectionChange={handlePropertySelectionChange}
          onSelectAll={handleSelectAllProperties}
          onDeselectAll={handleDeselectAllProperties}
          onEditProperty={handleEditProperty}
          onCopyProperty={handleCopyProperty}
          onDeleteProperty={handleDeleteProperty}
          onUpdateProperty={handleUpdateProperty}
          onCancelEdit={handleCancelEdit}
          editingPropertyId={editingPropertyId}
          formData={formData}
          onFormDataChange={handleInputChange}
          locations={locations}
        />
      </div>

      {/* Copy Property Form */}
      {showCopyForm && copyingProperty && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-2xl font-semibold text-gray-800 mb-6">
            Copy Property: {copyingProperty.name}
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Basic Property Info */}
            <div>
              <h3 className="text-lg font-semibold text-gray-700 mb-4">Basic Information</h3>
              {renderInputField('name', 'Property Name')}
              {renderInputField('address', 'Address')}
              {renderInputField('purchase_date', 'Purchase Date', 'date')}
              {renderInputField('rental_start_date', 'Rental Start Date', 'date')}
              {renderInputField('prices_as_of', 'Prices As Of (date rents & costs were entered)', 'date')}
              {renderInputField('purchase_price_eur', 'Purchase Price (€)', 'number')}
              {renderInputField('annual_appreciation_pct', 'Property Growth (%/yr, nominal - price to purchase and sale)', 'number')}
            </div>

            {/* Purchase Costs */}
            <div>
              <h3 className="text-lg font-semibold text-gray-700 mb-4">Purchase Costs</h3>
              {renderInputField('notaire_fees_pct', 'Notaire Fees (%)', 'number')}
              {renderInputField('renovation_refurbishment_eur', 'Renovation & Refurbishment (€)', 'number')}
              {renderInputField('furnishing_equipment_eur', 'Furnishing & Equipment (€)', 'number')}
              {renderInputField('legal_administrative_eur', 'Legal & Administrative (€)', 'number')}
              {renderInputField('property_transfer_tax_pct', 'Property Transfer Tax (%)', 'number')}
            </div>

            {/* Sale Details */}
            <div>
              <h3 className="text-lg font-semibold text-gray-700 mb-4">Sale Details</h3>
              {renderInputField('sale_date', 'Sale Date', 'date')}
              {renderInputField('sale_price_eur', 'Sale Price (€)', 'number')}

              {renderInputField('sale_notaire_fees_pct', 'Sale Notaire Fees (%)', 'number')}
              {renderInputField('sale_legal_fees_eur', 'Sale Legal Fees (€)', 'number')}
              {renderInputField('sale_agency_fees_pct', 'Sale Agency Fees (%)', 'number')}
              {renderInputField('sale_other_fees_eur', 'Other Sale Fees (€)', 'number')}
            </div>

            {/* Mortgage Details */}
            <div>
              <h3 className="text-lg font-semibold text-gray-700 mb-4">Mortgage Details</h3>
              {renderInputField('mortgage_rate_pct', 'Mortgage Rate (%)', 'number')}
              {renderInputField('mortgage_term_years', 'Mortgage Term (Years)', 'number')}
              {renderInputField('mortgage_interest_only_years', 'Interest-Only Years (0 = repayment from the start)', 'number')}
              {renderInputField('mortgage_step_after_years', 'Rate Step After (Years, 0 = none)', 'number')}
              {renderInputField('mortgage_step_rate_pct', 'Rate After the Step (%)', 'number')}
              {renderInputField('mortgage_arrangement_fee_eur', 'Arrangement Fee (€)', 'number')}
              <div className="mb-4 flex items-center gap-2">
                <input
                  type="checkbox"
                  id="mortgage_fee_added_to_loan"
                  checked={Boolean(Number(formData.mortgage_fee_added_to_loan))}
                  onChange={(e) => handleInputChange('mortgage_fee_added_to_loan', e.target.checked ? 1 : 0)}
                />
                <label htmlFor="mortgage_fee_added_to_loan" className="text-sm text-gray-700">Fee added to the loan (otherwise paid in cash at purchase)</label>
              </div>
              {renderInputField('deposit_eur', 'Deposit (€)', 'number')}
            </div>

            {/* Operating Costs */}
            <div>
              <h3 className="text-lg font-semibold text-gray-700 mb-4">Operating Costs</h3>
              {renderInputField('management_fee_pct', 'Management Fee (%)', 'number')}
              {renderInputField('cleaning_per_booking_eur', 'Cleaning per Booking (€)', 'number')}
              {renderInputField('average_stay_length_days', 'Average Stay Length (days)', 'number')}
              {renderInputField('utilities_monthly_eur', 'Utilities Monthly (€)', 'number')}
              {renderInputField('repairs_monthly_eur', 'Repairs Monthly (€)', 'number')}
              {renderInputField('tourist_tax_per_guest_night_eur', 'Tourist Tax per Guest-Night (€)', 'number')}
              {renderInputField('condo_fee_monthly_eur', 'Condo Fees Monthly (€)', 'number')}
              {renderInputField('taxe_fonciere_annual_eur', 'Taxe Foncière Annual (€)', 'number')}
              {renderInputField('insurance_annual_eur', 'Insurance Annual (€)', 'number')}
              {renderInputField('accountant_france_annual_eur', 'French Accountant Annual (€)', 'number')}
              {renderInputField('accountant_uk_annual_eur', 'UK Accountant Annual (€)', 'number')}
              {renderInputField('capex_provision_annual_pct', 'Capex Provision Annual (%)', 'number')}
            </div>
          </div>

          {/* Location Selection */}
          {renderLocationSelector()}

          <div className="flex gap-3 mt-6">
            <button
              onClick={handleAddProperty}
              disabled={loading}
              className="bg-vista-teal-600 text-white px-6 py-2 rounded-md hover:bg-vista-teal-700 transition-colors"
            >
              {loading ? 'Creating...' : 'Create Copy'}
            </button>
            <button
              onClick={() => {
                setShowCopyForm(false);
                setCopyingProperty(null);
                setFormData({
                  name: '', address: '', purchase_date: '', rental_start_date: '', prices_as_of: '', purchase_price_eur: 0,
                  purchase_costs_eur: 0,
                  annual_appreciation_pct: 3.0,
                  land_percentage_pct: 20.0, furniture_value_eur: 15000, mortgage_rate_pct: 3.6,
                  mortgage_term_years: 20, deposit_eur: 0, notaire_fees_pct: 7.5,
                  renovation_refurbishment_eur: 25000, furnishing_equipment_eur: 15000,
                  legal_administrative_eur: 3000, property_transfer_tax_pct: 5.8,
               sale_notaire_fees_pct: 0.0,
                  sale_legal_fees_eur: 2000, sale_agency_fees_pct: 5.0, management_fee_pct: 25.0,
                  cleaning_per_booking_eur: 50.0, average_stay_length_days: 3.0, utilities_monthly_eur: 200.0,
                  repairs_monthly_eur: 150.0, tourist_tax_per_guest_night_eur: 3.0,
                  condo_fee_monthly_eur: 0.0, taxe_fonciere_annual_eur: 0.0,
                  insurance_annual_eur: 0.0, accountant_france_annual_eur: 0.0,
                  accountant_uk_annual_eur: 0.0, capex_provision_annual_pct: 1.5, location_id: '',
                  occupancy_uncertainty_low_season_std_dev_pct: 8.0, occupancy_uncertainty_high_season_std_dev_pct: 12.0,
                  occupancy_uncertainty_event_std_dev_pct: 10.0, occupancy_uncertainty_film_festival_std_dev_pct: 2.0,
                  economic_impact_std_dev_pct: 15.0, economic_correlation: 1.0,
                  property_value_growth_std_dev_pct: 7.0, summer_weather_std_dev_pct: 20.0, weather_correlation: 0.8,
                  mc_antithetic_variables: true, mc_occupancy_scenarios: 100, mc_scenario_jitter_std_dev_pct: 5.0,
                  mc_random_seed: 12345, eur_gbp_volatility_pct: 12.0, currency_correlation: 0.3,
                  competitor_supply_increase_std_dev_pct: 20.0, economic_recession_impact_std_dev_pct: 30.0,
                  tourism_demand_shock_std_dev_pct: 25.0, major_repair_events_annual_prob: 0.15,
                  property_damage_annual_prob: 0.05, regulatory_changes_prob: 0.1, management_company_failure_prob: 0.05,
                  legal_issues_annual_prob: 0.08, bad_guest_incidents_annual_prob: 0.12, french_tax_regime: 'Réel',
                  fx_rate_income: 0.86, fx_rate_purchase_uk: 0.86, fx_rate_sale_uk: 0.86, years_owned: 10, uk_income_tax_rate_pct: 40.0, uk_cgt_annual_exempt_gbp: 3000,
                  total_building_depreciation_claimed_eur: 0.0
                });
              }}
              className="bg-gray-500 text-white px-6 py-2 rounded-md hover:bg-gray-600 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Analysis Results */}
      {analysisResults && (
        <div className="bg-white rounded-lg shadow-md p-6 mt-8">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4">Portfolio Analysis Results</h2>
          
          {/* Summary Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-vista-teal-600">
                €{analysisResults.total_investment?.toLocaleString() || 0}
              </div>
              <div className="text-sm text-gray-600">Total Investment (asset)</div>
              <div className="text-xs text-gray-500 mt-1">
                Cash in: €{analysisResults.total_cash_invested?.toLocaleString() || 0}
              </div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-vista-teal-600">
                €{analysisResults.total_projected_value?.toLocaleString() || 0}
              </div>
              <div className="text-sm text-gray-600">Projected Value</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-vista-teal-600">
                €{analysisResults.total_return?.toLocaleString() || 0}
              </div>
              <div className="text-sm text-gray-600">Total Return (gross)</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-vista-teal-600">
                {analysisResults.annualized_return_pct?.toFixed(2) || 0}%
              </div>
              <div className="text-sm text-gray-600">Annualized Return (gross)</div>
            </div>
          </div>

          {/* After-tax bottom line (F13) */}
          <div className="mb-6 p-4 rounded-lg bg-vista-teal-50 border border-vista-teal-200 text-center">
            <div className="text-3xl font-bold text-vista-teal-700">
              €{Math.round(analysisResults.final_after_tax_total || 0).toLocaleString()}
            </div>
            <div className="text-sm text-gray-600">
              Cumulative after-tax total over the analysis (net of French &amp; UK income tax, social levy and CGT)
            </div>
            {/* F16: money-weighted return on the dated after-tax flows */}
            <div className="mt-3 text-xl font-semibold text-vista-teal-700">
              {analysisResults.irr_after_tax_pct != null
                ? `IRR after tax: ${analysisResults.irr_after_tax_pct.toFixed(2)}% a year`
                : 'IRR after tax: not computed'}
            </div>
            {analysisResults.irr_after_tax_pct == null && analysisResults.irr_note && (
              <div className="text-xs text-gray-500">{analysisResults.irr_note}</div>
            )}
          </div>

          {/* Planner summary (F12): the three figures a retirement planner needs */}
          {analysisResults.planner_summary && (
            <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <div className="text-xl font-semibold text-gray-800">
                  €{Math.round(analysisResults.planner_summary.cash_invested_eur || 0).toLocaleString()}
                </div>
                <div className="text-xs text-gray-600">Cash invested (deposit + costs)</div>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <div className="text-xl font-semibold text-gray-800">
                  €{Math.round(analysisResults.planner_summary.steady_state_annual_net_eur || 0).toLocaleString()}
                </div>
                <div className="text-xs text-gray-600">Typical annual net (after income tax)</div>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <div className="text-xl font-semibold text-gray-800">
                  €{Math.round(analysisResults.planner_summary.net_sale_proceeds_eur || 0).toLocaleString()}
                </div>
                <div className="text-xs text-gray-600">Net sale proceeds (after CGT)</div>
              </div>
            </div>
          )}

          {/* Detailed Cashflow Reports - Identical to HTML Reports */}
          {analysisResults && (
            <div className="mt-6">
              <h3 className="text-lg font-semibold text-gray-700 mb-3">
                Detailed Cashflow Analysis - {cashflowGranularity.charAt(0).toUpperCase() + cashflowGranularity.slice(1)} Breakdown
              </h3>

              <TaxAssumptionNote className="mb-4" />
              <AssumedDefaultsNotice assumed={analysisResults?.assumed_defaults} className="mb-4" />

              {/* Granularity buttons removed per requirements */}

              {/* Summary Statistics */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
                <div className="text-center">
                  <div className="text-lg font-semibold text-gray-800">
                    €{analysisResults.total_revenue?.toLocaleString() || 0}
                  </div>
                  <div className="text-sm text-gray-600">Total Revenue</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-semibold text-gray-800">
                    €{analysisResults.total_expenses?.toLocaleString() || 0}
                  </div>
                  <div className="text-sm text-gray-600">Total Expenses</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-semibold text-gray-800">
                    €{analysisResults.total_net_cashflow?.toLocaleString() || 0}
                  </div>
                  <div className="text-sm text-gray-600">Total Net Cashflow</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-semibold text-gray-800">
                    {analysisResults.average_monthly_cashflow?.toFixed(0) || 0}
                  </div>
                  <div className="text-sm text-gray-600">Avg Monthly Cashflow</div>
                </div>
              </div>

              {/* Cashflow charts */}
              <CashflowCharts periods={analysisResults.cashflow_breakdown} />

                                                                          {/* Comprehensive Cashflow Table */}
                  <div className="max-h-[480px] overflow-auto border border-gray-200 rounded-lg">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-vista-teal-600 text-white">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider sticky top-0 left-0 bg-vista-teal-700 z-[70] border-r-2 border-white shadow-lg">
                            {cashflowGranularity === 'daily' ? 'Date' : cashflowGranularity === 'monthly' ? 'Month' : 'Year'}
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider sticky top-0 bg-vista-teal-600 z-50">
                            Event Days Occupied
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider sticky top-0 bg-vista-teal-600 z-50">
                            Event Days Unoccupied
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider sticky top-0 bg-vista-teal-600 z-50">
                            Non Event Days Occupied
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider sticky top-0 bg-vista-teal-600 z-50">
                            Non Event Days Unoccupied
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider sticky top-0 bg-vista-teal-600 z-50">
                            Owner Days
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider sticky top-0 bg-vista-teal-600 z-50">
                            ADR
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider sticky top-0 bg-vista-teal-600 z-50">
                            Occupancy
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider sticky top-0 bg-vista-teal-600 z-50">
                            Nights
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider sticky top-0 bg-vista-teal-600 z-50">
                            Revenue
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider sticky top-0 bg-vista-teal-600 z-50">
                            Management Fees
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider sticky top-0 bg-vista-teal-600 z-50">
                            Utilities
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider sticky top-0 bg-vista-teal-600 z-50">
                            Repairs
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider sticky top-0 bg-vista-teal-600 z-50">
                            Cleaning
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider sticky top-0 bg-vista-teal-600 z-50">
                            Tourist Tax
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider sticky top-0 bg-vista-teal-600 z-50">
                            Condo Fees
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider sticky top-0 bg-vista-teal-600 z-50">
                            Taxe Fonciere
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider sticky top-0 bg-vista-teal-600 z-50">
                            Insurance
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider sticky top-0 bg-vista-teal-600 z-50">
                            Accountant France
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider sticky top-0 bg-vista-teal-600 z-50">
                            Accountant UK
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider sticky top-0 bg-vista-teal-600 z-50">
                            Mortgage Interest
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider sticky top-0 bg-vista-teal-600 z-50">
                            Mortgage Principal
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider sticky top-0 bg-vista-teal-600 z-50">
                            Capex Provision
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider sticky top-0 bg-vista-teal-600 z-50">
                            Deposit Outflow
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider sticky top-0 bg-vista-teal-600 z-50">
                            Notaire Fees
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider sticky top-0 bg-vista-teal-600 z-50">
                            Legal Costs
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider sticky top-0 bg-vista-teal-600 z-50">
                            Transfer Tax
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider sticky top-0 bg-vista-teal-600 z-50">
                            Renovation Costs
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider sticky top-0 bg-vista-teal-600 z-50">
                            Furnishing Costs
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider sticky top-0 bg-vista-teal-600 z-50">
                            Sale Proceeds
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider sticky top-0 bg-vista-teal-600 z-50">
                            Sale Price
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider sticky top-0 bg-vista-teal-600 z-50">
                            Sale Costs
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider sticky top-0 bg-vista-teal-600 z-50">
                            Mortgage Redemption
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider sticky top-0 bg-vista-teal-600 z-50" title="Income tax falling due after the end date, settled in the last period (already inside the tax columns)">
                            Unpaid Tax Settled At End
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider sticky top-0 bg-vista-teal-600 z-50">
                            Earnings Before Tax
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider sticky top-0 bg-vista-teal-600 z-50">
                            French Income Tax
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider sticky top-0 bg-vista-teal-600 z-50">
                            French Social Levy
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider sticky top-0 bg-vista-teal-600 z-50">
                            UK Income Tax
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider sticky top-0 bg-vista-teal-600 z-50">
                            Total Expenses
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider sticky top-0 bg-vista-teal-600 z-50">
                            Net Cashflow
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider sticky top-0 bg-vista-teal-600 z-50">
                            Cumulative
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                    {analysisResults?.cashflow_breakdown && analysisResults.cashflow_breakdown.length > 0 ? (
                      analysisResults.cashflow_breakdown.map((period: any, index: number) => (
                      <tr key={index} className={`hover:bg-gray-50 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 sticky left-0 bg-white border-r">
                          {period.date || period.month || period.year}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right">
                          {period.event_days_occupied || 0}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right">
                          {period.event_days_unoccupied || 0}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right">
                          {period.non_event_days_occupied || 0}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right">
                          {period.non_event_days_unoccupied || 0}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right">
                          {period.owner_days || 0}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right">
                          €{period.average_daily_rate?.toFixed(0) || 0}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right">
                          {period.occupancy_rate?.toFixed(1) || 0}%
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right">
                          {period.total_nights || 0}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right">
                          €{Math.round(period.revenue || 0).toLocaleString()}
                        </td>
                        <td className={`px-4 py-3 whitespace-nowrap text-sm text-right ${(period.management_fees || 0) < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                          {formatCurrency(period.management_fees || 0)}
                        </td>
                        <td className={`px-4 py-3 whitespace-nowrap text-sm text-right ${(period.utilities || 0) < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                          {formatCurrency(period.utilities || 0)}
                        </td>
                        <td className={`px-4 py-3 whitespace-nowrap text-sm text-right ${(period.repairs || 0) < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                          {formatCurrency(period.repairs || 0)}
                        </td>
                        <td className={`px-4 py-3 whitespace-nowrap text-sm text-right ${(period.cleaning || 0) < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                          {formatCurrency(period.cleaning || 0)}
                        </td>
                        <td className={`px-4 py-3 whitespace-nowrap text-sm text-right ${(period.tourist_tax || 0) < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                          {formatCurrency(period.tourist_tax || 0)}
                        </td>
                        <td className={`px-4 py-3 whitespace-nowrap text-sm text-right ${(period.condo_fees || 0) < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                          {formatCurrency(period.condo_fees || 0)}
                        </td>
                        <td className={`px-4 py-3 whitespace-nowrap text-sm text-right ${(period.taxe_fonciere || 0) < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                          {formatCurrency(period.taxe_fonciere || 0)}
                        </td>
                        <td className={`px-4 py-3 whitespace-nowrap text-sm text-right ${(period.insurance || 0) < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                          {formatCurrency(period.insurance || 0)}
                        </td>
                        <td className={`px-4 py-3 whitespace-nowrap text-sm text-right ${(period.accountant_france || 0) < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                          {formatCurrency(period.accountant_france || 0)}
                        </td>
                        <td className={`px-4 py-3 whitespace-nowrap text-sm text-right ${(period.accountant_uk || 0) < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                          {formatCurrency(period.accountant_uk || 0)}
                        </td>
                        <td className={`px-4 py-3 whitespace-nowrap text-sm text-right ${(period.mortgage_interest || 0) < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                          {formatCurrency(period.mortgage_interest || 0)}
                        </td>
                        <td className={`px-4 py-3 whitespace-nowrap text-sm text-right ${(period.mortgage_principal || 0) < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                          {formatCurrency(period.mortgage_principal || 0)}
                        </td>
                        <td className={`px-4 py-3 whitespace-nowrap text-sm text-right ${(period.capex_provision || 0) < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                          {formatCurrency(period.capex_provision || 0)}
                        </td>
                        <td className={`px-4 py-3 whitespace-nowrap text-sm text-right ${(period.deposit_outflow || 0) < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                          {formatCurrency(period.deposit_outflow || 0)}
                        </td>
                        <td className={`px-4 py-3 whitespace-nowrap text-sm text-right ${(period.notaire_fees || 0) < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                          {formatCurrency(period.notaire_fees || 0)}
                        </td>
                        <td className={`px-4 py-3 whitespace-nowrap text-sm text-right ${(period.legal_costs || 0) < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                          {formatCurrency(period.legal_costs || 0)}
                        </td>
                        <td className={`px-4 py-3 whitespace-nowrap text-sm text-right ${(period.transfer_tax || 0) < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                          {formatCurrency(period.transfer_tax || 0)}
                        </td>
                        <td className={`px-4 py-3 whitespace-nowrap text-sm text-right ${(period.renovation_costs || 0) < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                          {formatCurrency(period.renovation_costs || 0)}
                        </td>
                        <td className={`px-4 py-3 whitespace-nowrap text-sm text-right ${(period.furnishing_costs || 0) < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                          {formatCurrency(period.furnishing_costs || 0)}
                        </td>
                        <td className={`px-4 py-3 whitespace-nowrap text-sm text-right ${(period.sale_proceeds || 0) < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                          {formatCurrency(period.sale_proceeds || 0)}
                        </td>
                        <td className={`px-4 py-3 whitespace-nowrap text-sm text-right ${(period.sale_price || 0) < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                          {formatCurrency(period.sale_price || 0)}
                        </td>
                        <td className={`px-4 py-3 whitespace-nowrap text-sm text-right ${(period.sale_costs || 0) < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                          {formatCurrency(period.sale_costs || 0)}
                        </td>
                        <td className={`px-4 py-3 whitespace-nowrap text-sm text-right ${(period.mortgage_redemption || 0) < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                          {formatCurrency(period.mortgage_redemption || 0)}
                        </td>
                        <td className={`px-4 py-3 whitespace-nowrap text-sm text-right ${(period.unpaid_tax_settled_eur || 0) < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                          {formatCurrency(period.unpaid_tax_settled_eur || 0)}
                        </td>
                        <td className={`px-4 py-3 whitespace-nowrap text-sm text-right ${(period.earnings_before_tax || 0) < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                          €{Math.round(period.earnings_before_tax || 0).toLocaleString()}
                        </td>
                        <td className={`px-4 py-3 whitespace-nowrap text-sm text-right ${(period.french_income_tax || 0) < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                          {formatCurrency(period.french_income_tax || 0)}
                        </td>
                        <td className={`px-4 py-3 whitespace-nowrap text-sm text-right ${(period.french_social_levy || 0) < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                          {formatCurrency(period.french_social_levy || 0)}
                        </td>
                        <td className={`px-4 py-3 whitespace-nowrap text-sm text-right ${(period.uk_income_tax_eur || 0) < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                          {formatCurrency(period.uk_income_tax_eur || 0)}
                        </td>
                        <td className={`px-4 py-3 whitespace-nowrap text-sm text-right font-medium ${(period.total_expenses || 0) < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                          {formatCurrency(period.total_expenses || 0)}
                        </td>
                        <td className={`px-4 py-3 whitespace-nowrap text-sm font-bold text-right ${
                          period.net_cashflow >= 0 ? 'text-green-600' : 'text-red-600'
                        }`}>
                          €{Math.round(period.net_cashflow || 0).toLocaleString()}
                        </td>
                        <td className={`px-4 py-3 whitespace-nowrap text-sm font-bold text-right ${
                          period.cumulative_cashflow >= 0 ? 'text-green-600' : 'text-red-600'
                        }`}>
                          €{Math.round(period.cumulative_cashflow || 0).toLocaleString()}
                        </td>
                      </tr>
                    ))
                    ) : (
                      <tr>
                        <td colSpan={42} className="px-4 py-8 text-center text-gray-500">
                          No cashflow data available. Please check the analysis results.
                        </td>
                      </tr>
                    )}
                  </tbody>
                  <tfoot className="bg-gray-100 border-t-2 border-gray-300">
                    <tr>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-gray-900 sticky left-0 bg-gray-100 border-r">
                        TOTALS
                      </td>
                      {/* Event/Owner Days - No totals needed */}
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-gray-900 text-right">-</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-gray-900 text-right">-</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-gray-900 text-right">-</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-gray-900 text-right">-</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-gray-900 text-right">-</td>
                      {/* ADR, Occupancy, Nights - No totals needed */}
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-gray-900 text-right">-</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-gray-900 text-right">-</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-gray-900 text-right">-</td>
                      {/* Revenue */}
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-gray-900 text-right">
                        €{Math.round(analysisResults.total_revenue || 0).toLocaleString()}
                      </td>
                      {/* All expense columns - show totals */}
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-gray-900 text-right">
                        €{Math.round(analysisResults.cashflow_breakdown?.reduce((sum: number, period: any) => sum + (period.management_fees || 0), 0) || 0).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-gray-900 text-right">
                        €{Math.round(analysisResults.cashflow_breakdown?.reduce((sum: number, period: any) => sum + (period.utilities || 0), 0) || 0).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-gray-900 text-right">
                        €{Math.round(analysisResults.cashflow_breakdown?.reduce((sum: number, period: any) => sum + (period.repairs || 0), 0) || 0).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-gray-900 text-right">
                        €{Math.round(analysisResults.cashflow_breakdown?.reduce((sum: number, period: any) => sum + (period.cleaning || 0), 0) || 0).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-gray-900 text-right">
                        €{Math.round(analysisResults.cashflow_breakdown?.reduce((sum: number, period: any) => sum + (period.tourist_tax || 0), 0) || 0).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-gray-900 text-right">
                        €{Math.round(analysisResults.cashflow_breakdown?.reduce((sum: number, period: any) => sum + (period.condo_fees || 0), 0) || 0).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-gray-900 text-right">
                        €{Math.round(analysisResults.cashflow_breakdown?.reduce((sum: number, period: any) => sum + (period.taxe_fonciere || 0), 0) || 0).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-gray-900 text-right">
                        €{Math.round(analysisResults.cashflow_breakdown?.reduce((sum: number, period: any) => sum + (period.insurance || 0), 0) || 0).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-gray-900 text-right">
                        €{Math.round(analysisResults.cashflow_breakdown?.reduce((sum: number, period: any) => sum + (period.accountant_france || 0), 0) || 0).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-gray-900 text-right">
                        €{Math.round(analysisResults.cashflow_breakdown?.reduce((sum: number, period: any) => sum + (period.accountant_uk || 0), 0) || 0).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-gray-900 text-right">
                        €{Math.round(analysisResults.cashflow_breakdown?.reduce((sum: number, period: any) => sum + (period.mortgage_interest || 0), 0) || 0).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-gray-900 text-right">
                        €{Math.round(analysisResults.cashflow_breakdown?.reduce((sum: number, period: any) => sum + (period.mortgage_principal || 0), 0) || 0).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-gray-900 text-right">
                        €{Math.round(analysisResults.cashflow_breakdown?.reduce((sum: number, period: any) => sum + (period.capex_provision || 0), 0) || 0).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-gray-900 text-right">
                        €{Math.round(analysisResults.cashflow_breakdown?.reduce((sum: number, period: any) => sum + (period.deposit_outflow || 0), 0) || 0).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-gray-900 text-right">
                        €{Math.round(analysisResults.cashflow_breakdown?.reduce((sum: number, period: any) => sum + (period.notaire_fees || 0), 0) || 0).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-gray-900 text-right">
                        €{Math.round(analysisResults.cashflow_breakdown?.reduce((sum: number, period: any) => sum + (period.legal_costs || 0), 0) || 0).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-gray-900 text-right">
                        €{Math.round(analysisResults.cashflow_breakdown?.reduce((sum: number, period: any) => sum + (period.transfer_tax || 0), 0) || 0).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-gray-900 text-right">
                        €{Math.round(analysisResults.cashflow_breakdown?.reduce((sum: number, period: any) => sum + (period.renovation_costs || 0), 0) || 0).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-gray-900 text-right">
                        €{Math.round(analysisResults.cashflow_breakdown?.reduce((sum: number, period: any) => sum + (period.furnishing_costs || 0), 0) || 0).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-gray-900 text-right">
                        €{Math.round(analysisResults.cashflow_breakdown?.reduce((sum: number, period: any) => sum + (period.sale_proceeds || 0), 0) || 0).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-gray-900 text-right">
                        €{Math.round(analysisResults.cashflow_breakdown?.reduce((sum: number, period: any) => sum + (period.sale_price || 0), 0) || 0).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-gray-900 text-right">
                        €{Math.round(analysisResults.cashflow_breakdown?.reduce((sum: number, period: any) => sum + (period.sale_costs || 0), 0) || 0).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-gray-900 text-right">
                        €{Math.round(analysisResults.cashflow_breakdown?.reduce((sum: number, period: any) => sum + (period.mortgage_redemption || 0), 0) || 0).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-gray-900 text-right">
                        €{Math.round(analysisResults.cashflow_breakdown?.reduce((sum: number, period: any) => sum + (period.unpaid_tax_settled_eur || 0), 0) || 0).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-gray-900 text-right">
                        €{Math.round(analysisResults.cashflow_breakdown?.reduce((sum: number, period: any) => sum + (period.earnings_before_tax || 0), 0) || 0).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-gray-900 text-right">
                        €{Math.round(analysisResults.cashflow_breakdown?.reduce((sum: number, period: any) => sum + (period.french_income_tax || 0), 0) || 0).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-gray-900 text-right">
                        €{Math.round(analysisResults.cashflow_breakdown?.reduce((sum: number, period: any) => sum + (period.french_social_levy || 0), 0) || 0).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-gray-900 text-right">
                        €{Math.round(analysisResults.cashflow_breakdown?.reduce((sum: number, period: any) => sum + (period.uk_income_tax_eur || 0), 0) || 0).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-gray-900 text-right">
                        €{Math.round(analysisResults.total_expenses || 0).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-gray-900 text-right">
                        €{Math.round(analysisResults.total_net_cashflow || 0).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-gray-900 text-right">
                        €{Math.round(analysisResults.cashflow_breakdown && analysisResults.cashflow_breakdown.length > 0 
                          ? analysisResults.cashflow_breakdown[analysisResults.cashflow_breakdown.length - 1].cumulative_cashflow || 0
                          : 0).toLocaleString()}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* Export Options */}
              <div className="mt-6 flex gap-4">
                <button
                  onClick={() => exportToCSV()}
                  className="bg-green-600 text-white px-6 py-2 rounded-md hover:bg-green-700 transition-colors"
                >
                  Export to CSV
                </button>
                <button
                  onClick={() => exportToPDF()}
                  className="bg-red-600 text-white px-6 py-2 rounded-md hover:bg-red-700 transition-colors"
                >
                  Export to PDF
                </button>
                <button
                  onClick={() => exportForDebugging()}
                  className="bg-purple-600 text-white px-6 py-2 rounded-md hover:bg-purple-700 transition-colors"
                >
                  Export For Debugging
                </button>
              </div>

              {/* Cashflow Charts */}
              <div className="mt-6 grid grid-cols-1 gap-6">
                {/* Income vs Expenses (columns) */}
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="text-md font-semibold text-gray-700 mb-3">Total Income vs Expenses</h4>
                  {analysisResults.cashflow_breakdown && analysisResults.cashflow_breakdown.length > 0 ? (
                    (() => {
                      const points = analysisResults.cashflow_breakdown;
                      const incomes = points.map((p: any) => Math.max(0, Number(p.revenue || 0)));
                      const expenses = points.map((p: any) => Math.abs(Number(p.total_expenses || 0))); // show magnitude
                      const maxY = Math.max(1, ...incomes, ...expenses);
                      return (
                        <div className="overflow-x-auto">
                          <div className="min-w-[720px]">
                            <div className="flex items-end gap-2 h-48 border-b border-gray-300 pb-2">
                              {points.map((p: any, idx: number) => (
                                <div key={idx} className="flex flex-col items-center gap-1 w-8">
                                  <div className="flex gap-1 items-end w-full">
                                    <div className="bg-green-500 w-3" style={{ height: `${(incomes[idx] / maxY) * 100}%` }} title={`Income €${Math.round(incomes[idx]).toLocaleString()}`}></div>
                                    <div className="bg-red-500 w-3" style={{ height: `${(expenses[idx] / maxY) * 100}%` }} title={`Expenses €-${Math.round(expenses[idx]).toLocaleString()}`}></div>
                                  </div>
                                  <div className="text-[10px] text-gray-600 truncate max-w-[2rem]" title={p.date || p.month || p.year}>{(p.date || p.month || p.year)}</div>
                                </div>
                              ))}
                            </div>
                            <div className="flex justify-between text-xs text-gray-500 mt-1">
                              <span>0</span>
                              <span>Max €{Math.round(maxY).toLocaleString()}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })()
                  ) : (
                    <div className="text-sm text-gray-600">No data</div>
                  )}
                </div>

                {/* Cumulative Net Cashflow (columns) */}
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="text-md font-semibold text-gray-700 mb-3">Cumulative Net Cashflow</h4>
                  {analysisResults.cashflow_breakdown && analysisResults.cashflow_breakdown.length > 0 ? (
                    (() => {
                      const points = analysisResults.cashflow_breakdown;
                      const cum = points.map((p: any) => Number(p.cumulative_cashflow || 0));
                      const maxAbs = Math.max(1, ...cum.map((v: number) => Math.abs(v)));
                      return (
                        <div className="overflow-x-auto">
                          <div className="min-w-[720px]">
                            <div className="flex items-end gap-2 h-48 border-b border-gray-300 pb-2">
                              {points.map((p: any, idx: number) => (
                                <div key={idx} className="flex flex-col items-center gap-1 w-6">
                                  <div className={`w-4 ${cum[idx] >= 0 ? 'bg-green-600' : 'bg-red-600'}`} style={{ height: `${(Math.abs(cum[idx]) / maxAbs) * 100}%` }} title={`€${Math.round(cum[idx]).toLocaleString()}`}></div>
                                  <div className="text-[10px] text-gray-600 truncate max-w-[1.5rem]" title={p.date || p.month || p.year}>{(p.date || p.month || p.year)}</div>
                                </div>
                              ))}
                            </div>
                            <div className="flex justify-between text-xs text-gray-500 mt-1">
                              <span>0</span>
                              <span>Max |€{Math.round(maxAbs).toLocaleString()}|</span>
                            </div>
                          </div>
                        </div>
                      );
                    })()
                  ) : (
                    <div className="text-sm text-gray-600">No data</div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Fallback: Show Analysis Results Structure */}
          {analysisResults && !analysisResults.cashflow_breakdown && (
            <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <h4 className="text-md font-semibold text-yellow-700 mb-2">Analysis Results Available</h4>
              <p className="text-sm text-yellow-600 mb-3">
                Portfolio analysis completed, but detailed cashflow breakdown is not yet available.
              </p>
              <div className="bg-white p-3 rounded border">
                <h5 className="font-medium text-gray-800 mb-2">Available Data:</h5>
                <pre className="text-xs text-gray-600 overflow-auto">
                  {JSON.stringify(analysisResults, null, 2)}
                </pre>
              </div>
            </div>
          )}

          {/* Property-Specific Breakdown */}
          {analysisResults.property_breakdown && (
            <div className="mt-6">
              <h3 className="text-lg font-semibold text-gray-700 mb-3">Property-Specific Analysis</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {analysisResults.property_breakdown.map((property: any, index: number) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-800 mb-2">{property.name}</h4>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span>Investment:</span>
                        <span>€{Math.round(property.total_investment || 0).toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Projected Value:</span>
                        <span>€{Math.round(property.projected_value || 0).toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Total Return:</span>
                        <span>€{Math.round(property.total_return || 0).toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Annual Return:</span>
                        <span>{property.annualized_return_pct?.toFixed(2) || 0}%</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      </div>
    </div>
  );
};

export default PortfolioInvestmentCalculator;
